import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { CostReportsCacheService } from '../services/cost-reports-cache.service';
import { CostAnalysisStateService } from '../services/cost-analysis-state.service';
import { buildSearchCommand } from '../utils/filter-command.util';
import {
  ComponentSummaryReportDto,
  ComponentRowDto,
  ComponentTotalsDto,
  CostReportsProxyService,
} from '../../../proxies/cost-reports.proxy.service';
import { CostComponentKeys, CostSourceFilter, PROCESS_LABELS, ProcessKey, REAL_PROCESSES } from '../models/cost-analysis.models';

interface ComponentDisplayRow extends ComponentRowDto {
  isWasteParent?: boolean;
  isWasteChild?: boolean;
  isRawMaterial?: boolean;
  totalCostPerLbUsd: number;
}

@Component({
  selector: 'app-component-summary',
  templateUrl: './component-summary.component.html',
  styleUrl: './component-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComponentSummaryComponent {
  private readonly cache = inject(CostReportsCacheService);
  private readonly proxy = inject(CostReportsProxyService);
  private readonly state = inject(CostAnalysisStateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly year = input.required<number>();
  readonly month = input.required<number>();
  readonly costSource = input.required<CostSourceFilter>();

  readonly report = signal<ComponentSummaryReportDto | null>(null);
  readonly loading = signal(false);

  readonly processes: readonly ProcessKey[] = REAL_PROCESSES;
  readonly processLabels = PROCESS_LABELS;

  readonly expandedWaste = signal(false);

  readonly componentRows = computed<ComponentDisplayRow[]>(() => {
    const rep = this.report();
    if (!rep) return [];
    return rep.rows
      .map<ComponentDisplayRow>((r) => {
        const isWasteParent = r.component === CostComponentKeys.Waste && (r.children?.length ?? 0) > 0;
        const isRawMaterial = r.component === CostComponentKeys.RawMaterial;
        const base = isWasteParent ? this.sumChildren(r) : r;
        return {
          ...r,
          realCostUsd: base.realCostUsd,
          realPerLb: base.realPerLb,
          stdPerLb: base.stdPerLb,
          variancePerLb: base.variancePerLb,
          variancePct: base.variancePct,
          pctOfTotal: base.pctOfTotal,
          byProcess: base.byProcess,
          isWasteParent,
          isRawMaterial,
          totalCostPerLbUsd: this.processes.reduce((sum, p) => sum + (base.byProcess[p] ?? 0), 0),
        };
      })
      .filter((r) => r.realCostUsd > 0 || r.stdPerLb > 0);
  });

  private sumChildren(parent: ComponentRowDto): ComponentRowDto {
    const children = parent.children ?? [];
    const realCostUsd = children.reduce((s, c) => s + c.realCostUsd, 0);
    const realPerLb = children.reduce((s, c) => s + c.realPerLb, 0);
    const stdPerLb = children.reduce((s, c) => s + c.stdPerLb, 0);
    const variancePerLb = realPerLb - stdPerLb;
    const variancePct = stdPerLb > 0 ? (variancePerLb / stdPerLb) * 100 : null;
    const byProcess = {} as Record<string, number>;
    for (const p of this.processes) {
      byProcess[p] = children.reduce((s, c) => s + (c.byProcess[p] ?? 0), 0);
    }
    return {
      ...parent,
      realCostUsd,
      realPerLb,
      stdPerLb,
      variancePerLb,
      variancePct,
      pctOfTotal: parent.pctOfTotal,
      byProcess,
    };
  }

  readonly visibleRows = computed<ComponentDisplayRow[]>(() => {
    const rows = this.componentRows();
    if (!this.expandedWaste()) return rows;
    const out: ComponentDisplayRow[] = [];
    for (const row of rows) {
      out.push(row);
      if (row.isWasteParent && row.children) {
        for (const child of row.children) {
          out.push({
            ...child,
            isWasteChild: true,
            totalCostPerLbUsd: this.processes.reduce((sum, p) => sum + (child.byProcess[p] ?? 0), 0),
          });
        }
      }
    }
    return out;
  });

  readonly componentTotals = computed<ComponentTotalsDto>(() => {
    const rep = this.report();
    if (!rep) {
      return {
        totalCostUsd: 0,
        totalRealPerLb: 0,
        totalStdPerLb: 0,
        totalVariancePerLb: 0,
        totalVariancePct: null,
        pctOfTotal: 0,
        byProcess: {} as Record<ProcessKey, number>,
        sumOfProcessCostPerLb: 0,
      };
    }
    return rep.totals;
  });

  constructor() {
    effect(
      () => {
        const y = this.year();
        const m = this.month();
        const src = this.costSource();
        const filters = this.state.filters();
        if (!y || !m) {
          this.report.set(null);
          return;
        }
        const cmd = buildSearchCommand(src, filters);
        const obs = cmd
          ? this.proxy.searchComponentSummaryReport(y, m, cmd, filters.onlyCompletedRolls)
          : this.cache.getComponentSummary(y, m, src);
        this.loading.set(true);
        obs
          .pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef),
            finalize(() => this.loading.set(false)),
          )
          .subscribe({
            next: (resp) => this.report.set(resp.success && resp.data ? resp.data : null),
            error: () => this.report.set(null),
          });
      },
      { allowSignalWrites: true },
    );
  }

  toggleWasteExpand(): void {
    this.expandedWaste.update((v) => !v);
  }

  rowTotalCostPerLb(row: ComponentDisplayRow): number {
    return this.processes.reduce((sum, p) => sum + (row.byProcess[p] ?? 0), 0);
  }
}
